// src/components/layout/UserAccessSummary.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IdCard, Warehouse, Check } from "lucide-react";
import { ALL_ACCESS_TOKEN, NULL_ACCESS_TOKEN, getUserAccessInfo } from "../../utils/accessFilters";
import { setAccessOverrideForType } from "../../utils/accessPreferenceStore";
import useAccessOverrideVersion from "../../hooks/useAccessOverrideVersion";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const MASTER_ENDPOINTS = {
  cust: "/customers",
  whs: "/warehouses",
};
const MASTER_KEYS = {
  cust: ["cust_code", "custCode", "customer_code", "customerCode", "code", "company_code", "companyCode"],
  whs: [
    "whse_code",
    "whseCode",
    "warehouse_code",
    "warehouseCode",
    "code",
    "company_code",
    "companyCode",
    "wms_code",
    "wmsCode",
  ],
};

const masterCache = {
  cust: { tokens: null, error: "", promise: null },
  whs: { tokens: null, error: "", promise: null },
};

const toUpperSortedUnique = (values = []) => {
  const seen = new Set();
  values.forEach((value) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return;
    const upper = trimmed.toUpperCase();
    if (!seen.has(upper)) {
      seen.add(upper);
    }
  });
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
};

const fetchMasterTokens = async (type, user) => {
  const cache = masterCache[type];
  if (!cache) return { tokens: [], error: "Unknown access type." };
  if (cache.tokens) {
    return { tokens: cache.tokens, error: cache.error || "" };
  }
  if (cache.promise) {
    return cache.promise;
  }
  const headers = {};
  if (user?.email) headers["X-User-Email"] = user.email;
  if (user?.role) headers["X-User-Role"] = user.role || "";
  const endpoint = MASTER_ENDPOINTS[type];
  const keyCandidates = MASTER_KEYS[type] || [];
  cache.promise = fetch(`${API_BASE}${endpoint}`, { headers })
    .then(async (resp) => {
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to load access list.");
      }
      const rows = Array.isArray(payload.rows) ? payload.rows : [];
      const collected = rows.flatMap((row) =>
        keyCandidates.map((key) => row?.[key]).filter(Boolean),
      );
      const tokens = toUpperSortedUnique(collected);
      cache.tokens = tokens;
      cache.error = "";
      return { tokens, error: "" };
    })
    .catch((err) => {
      const message = err?.message || "Unable to load access list.";
      cache.tokens = null;
      cache.error = message;
      return { tokens: [], error: message };
    })
    .finally(() => {
      cache.promise = null;
    });
  return cache.promise;
};

const sortTokens = (a, b) => {
  if (a === ALL_ACCESS_TOKEN) return -1;
  if (b === ALL_ACCESS_TOKEN) return 1;
  return a.localeCompare(b);
};

const buildAvailableTokens = (baseTokens = [], activeTokens = []) => {
  const set = new Set();
  (baseTokens || []).forEach((token) => token && set.add(token));
  (activeTokens || []).forEach((token) => token && set.add(token));
  if (set.has(ALL_ACCESS_TOKEN)) return [ALL_ACCESS_TOKEN];
  return Array.from(set).sort(sortTokens);
};

const summariseAccess = (activeTokens = [], hasOverride, emptyDetail) => {
  if (!activeTokens.length) {
    return hasOverride
      ? { label: "0 selected", detail: "No data visible (custom filter)" }
      : { label: "None", detail: emptyDetail };
  }
  if (activeTokens.includes(ALL_ACCESS_TOKEN)) {
    return { label: ALL_ACCESS_TOKEN, detail: "Full access" };
  }
  if (!hasOverride && activeTokens.length > 1) {
    return { label: ALL_ACCESS_TOKEN, detail: activeTokens.join(", ") };
  }
  if (activeTokens.length === 1) {
    return { label: activeTokens[0], detail: activeTokens[0] };
  }
  return { label: `${activeTokens.length} selected`, detail: activeTokens.join(", ") };
};

const buildAccessState = ({
  baseTokens = [],
  activeTokens = [],
  hasOverride = false,
  emptyDetail,
  universeTokens = [],
  universeError = "",
  universeLoading = false,
}) => {
  const hasAdminAll = baseTokens.includes(ALL_ACCESS_TOKEN);
  let availableTokens;
  if (hasAdminAll) {
    const combined =
      universeTokens.length > 0
        ? [...universeTokens, NULL_ACCESS_TOKEN]
        : [ALL_ACCESS_TOKEN, NULL_ACCESS_TOKEN];
    availableTokens = toUpperSortedUnique(combined);
  } else {
    availableTokens = buildAvailableTokens(baseTokens, activeTokens);
  }
  return {
    summary: summariseAccess(activeTokens, hasOverride, emptyDetail),
    availableTokens,
    activeTokens: [...activeTokens],
    hasOverride,
    emptyDetail,
    hasAdminAll,
    universeError: hasAdminAll && !universeTokens.length ? universeError : "",
    universeLoading: hasAdminAll && !universeTokens.length && universeLoading,
    showAdminAllPlaceholder: hasAdminAll && !universeTokens.length,
  };
};

const QuickButton = ({ label, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`rounded-full border border-slate-200 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 ${
      disabled ? "cursor-not-allowed opacity-40" : ""
    }`}
  >
    {label}
  </button>
);

const AccessDropdownButton = ({
  icon,
  colorClasses,
  summary,
  availableTokens,
  activeTokens,
  hasOverride,
  emptyMessage,
  menuLabel,
  onToggle,
  onSelectAll,
  onClear,
  isLoadingUniverse = false,
  universeError = "",
  showAdminAllPlaceholder = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonIdRef = useRef(`access-btn-${Math.random().toString(36).slice(2, 8)}`);
  const menuIdRef = useRef(`access-menu-${Math.random().toString(36).slice(2, 8)}`);
  const menuPanelRef = useRef(null);
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });

  const updateMenuCoords = useCallback(() => {
    const host = containerRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const panelWidth = 260;
    let left = rect.left;
    if (left + panelWidth > viewportWidth - 8) left = Math.max(8, viewportWidth - panelWidth - 8);
    if (left < 8) left = 8;
    const top = rect.bottom + 8;
    setMenuCoords({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (
        !containerRef.current?.contains(event.target) &&
        !menuPanelRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuCoords();
    const handleResize = () => updateMenuCoords();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [open, updateMenuCoords]);

  const selection = useMemo(() => new Set(activeTokens || []), [activeTokens]);
  const emptyList = availableTokens.length === 0;

  const handleItemToggle = (token) => {
    onToggle(token);
  };

  return (
    <div className="relative inline-flex z-[120]" ref={containerRef}>
      <button
        type="button"
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 ${colorClasses} ${
          hasOverride ? "shadow-[0_0_0_1px_rgba(59,130,246,0.4)]" : ""
        }`}
        title={summary.detail}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuIdRef.current}
        id={buttonIdRef.current}
        onClick={() => setOpen((prev) => !prev)}
      >
        {icon}
        <span>{summary.label}</span>
      </button>
      {open &&
        createPortal(
          <div
            role="menu"
            aria-labelledby={buttonIdRef.current}
            id={menuIdRef.current}
            ref={menuPanelRef}
            className="fixed z-[9999] min-w-[240px] rounded-xl border border-slate-200 bg-white/95 p-3 text-sm shadow-2xl backdrop-blur"
            style={{ top: `${menuCoords.top}px`, left: `${menuCoords.left}px` }}
          >
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{menuLabel}</p>
            <div className="mb-2 flex flex-wrap gap-2">
              <QuickButton label="Select all" onClick={onSelectAll} disabled={emptyList && !isLoadingUniverse} />
              <QuickButton label="Clear" onClick={onClear} disabled={selection.size === 0 && !hasOverride} />
            </div>
            <ul className="max-h-60 space-y-1 overflow-auto text-xs">
              {isLoadingUniverse ? (
                <li className="rounded-lg bg-slate-50 px-2 py-2 text-slate-400">Loading master list…</li>
              ) : emptyList ? (
                <li className="rounded-lg bg-slate-50 px-2 py-2 text-slate-400">{emptyMessage}</li>
              ) : (
                availableTokens.map((token) => {
                  const label =
                    token === ALL_ACCESS_TOKEN
                      ? "ALL (full access)"
                      : token === NULL_ACCESS_TOKEN
                        ? "Null"
                        : token;
                  const checked = selection.has(token);
                  return (
                    <li key={token}>
                      <button
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={checked}
                        onClick={() => handleItemToggle(token)}
                        className={`flex w-full items-center justify-between rounded-lg px-2 py-1 text-left text-slate-700 transition hover:bg-slate-50 ${
                          checked ? "font-semibold text-slate-900" : ""
                        }`}
                      >
                        <span>{label}</span>
                        <Check
                          className={`h-3.5 w-3.5 ${checked ? "text-emerald-500 opacity-100" : "text-slate-300 opacity-0"}`}
                        />
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
            {universeError && !isLoadingUniverse && (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                {universeError}
              </p>
            )}
            {showAdminAllPlaceholder && !isLoadingUniverse && !universeError && (
              <p className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
                Admin has granted ALL access.
              </p>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default function UserAccessSummary({ className = "" }) {
  useAccessOverrideVersion();
  const {
    user,
    custTokens = [],
    whsTokens = [],
    activeCustTokens = [],
    activeWhsTokens = [],
    hasCustOverride,
    hasWhsOverride,
  } = getUserAccessInfo();
  const userEmail = user?.email || "";
  const userRole = user?.role || "";
  const hasUserIdentity = Boolean(userEmail || userRole);
  const userIdentity = useMemo(
    () => ({ email: userEmail, role: userRole }),
    [userEmail, userRole],
  );

  const baseCustIsAll = custTokens.includes(ALL_ACCESS_TOKEN);
  const baseWhsIsAll = whsTokens.includes(ALL_ACCESS_TOKEN);

  const [custUniverse, setCustUniverse] = useState([]);
  const [custUniverseError, setCustUniverseError] = useState("");
  const [custUniverseLoading, setCustUniverseLoading] = useState(false);
  const [whsUniverse, setWhsUniverse] = useState([]);
  const [whsUniverseError, setWhsUniverseError] = useState("");
  const [whsUniverseLoading, setWhsUniverseLoading] = useState(false);

  useEffect(() => {
    if (!hasUserIdentity || !baseCustIsAll) {
      setCustUniverse([]);
      setCustUniverseError("");
      setCustUniverseLoading(false);
      return;
    }
    let cancelled = false;
    setCustUniverseLoading(true);
    fetchMasterTokens("cust", userIdentity)
      .then(({ tokens, error }) => {
        if (cancelled) return;
        setCustUniverse(tokens || []);
        setCustUniverseError(error || "");
      })
      .finally(() => {
        if (!cancelled) setCustUniverseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasUserIdentity, userIdentity, baseCustIsAll]);

  useEffect(() => {
    if (!hasUserIdentity || !baseWhsIsAll) {
      setWhsUniverse([]);
      setWhsUniverseError("");
      setWhsUniverseLoading(false);
      return;
    }
    let cancelled = false;
    setWhsUniverseLoading(true);
    fetchMasterTokens("whs", userIdentity)
      .then(({ tokens, error }) => {
        if (cancelled) return;
        setWhsUniverse(tokens || []);
        setWhsUniverseError(error || "");
      })
      .finally(() => {
        if (!cancelled) setWhsUniverseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasUserIdentity, userIdentity, baseWhsIsAll]);

  const custState = useMemo(
    () =>
      buildAccessState({
        baseTokens: custTokens,
        activeTokens: activeCustTokens,
        hasOverride: hasCustOverride,
        emptyDetail: "No customer access assigned",
        universeTokens: baseCustIsAll ? custUniverse : [],
        universeError: custUniverseError,
        universeLoading: custUniverseLoading,
      }),
    [
      custTokens,
      activeCustTokens,
      hasCustOverride,
      baseCustIsAll,
      custUniverse,
      custUniverseError,
      custUniverseLoading,
    ],
  );

  const whsState = useMemo(
    () =>
      buildAccessState({
        baseTokens: whsTokens,
        activeTokens: activeWhsTokens,
        hasOverride: hasWhsOverride,
        emptyDetail: "No warehouse access assigned",
        universeTokens: baseWhsIsAll ? whsUniverse : [],
        universeError: whsUniverseError,
        universeLoading: whsUniverseLoading,
      }),
    [whsTokens, activeWhsTokens, hasWhsOverride, baseWhsIsAll, whsUniverse, whsUniverseError, whsUniverseLoading],
  );

  if (!user) return null;

  const persistSelection = (type, tokens) => {
    setAccessOverrideForType(user, type, tokens);
  };

const buildHandlers = (type, state) => ({
  onToggle: (token) => {
    if (token === ALL_ACCESS_TOKEN) {
      persistSelection(type, [ALL_ACCESS_TOKEN]);
      return;
    }
    const next = new Set(state.activeTokens);
    if (next.has(token)) next.delete(token);
    else next.add(token);
    next.delete(ALL_ACCESS_TOKEN);
    persistSelection(type, Array.from(next));
  },
  onSelectAll: () => {
    if (!state.availableTokens.length) return;
    if (state.availableTokens.length === 1 && state.availableTokens[0] === ALL_ACCESS_TOKEN) {
      persistSelection(type, [ALL_ACCESS_TOKEN]);
      return;
    }
    persistSelection(type, state.availableTokens);
  },
  onClear: () => persistSelection(type, []),
});

  const custHandlers = buildHandlers("cust", custState);
  const whsHandlers = buildHandlers("whs", whsState);

  return (
    <div className={`relative z-40 flex items-center gap-2 text-slate-600 ${className}`}>
      <AccessDropdownButton
        icon={<IdCard className="h-4 w-4 text-emerald-500" />}
        colorClasses="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
      summary={custState.summary}
      availableTokens={custState.availableTokens}
      activeTokens={custState.activeTokens}
      hasOverride={custState.hasOverride}
      emptyMessage="No customer access assigned"
      menuLabel="Customer Access"
      isLoadingUniverse={custState.universeLoading}
      universeError={custState.universeError}
      showAdminAllPlaceholder={custState.showAdminAllPlaceholder}
      {...custHandlers}
    />
    <AccessDropdownButton
      icon={<Warehouse className="h-4 w-4 text-rose-500" />}
      colorClasses="border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
      summary={whsState.summary}
      availableTokens={whsState.availableTokens}
      activeTokens={whsState.activeTokens}
      hasOverride={whsState.hasOverride}
      emptyMessage="No warehouse access assigned"
      menuLabel="Warehouse Access"
      isLoadingUniverse={whsState.universeLoading}
      universeError={whsState.universeError}
      showAdminAllPlaceholder={whsState.showAdminAllPlaceholder}
      {...whsHandlers}
    />
    </div>
  );
}
